import * as React from "react";
import {
  Position,
  SwanPublicClient,
  StreamUpdate,
  StreamUpdateType,
  Currency
} from "swan-public-api-client";
import { SubscriptionLifecycle } from "../Push/SubscriptionLifecycle";
import { FxConvertedPosition } from "./FxConvertedPosition";
import { connect } from "react-redux";
import { AppState } from "../Store/RootReducer";
import { FxRates } from "../Api/FxRates";

interface PropsFromAppState {
  baseCurrency: Currency;
}

interface Props extends PropsFromAppState {
  children: (positions: FxConvertedPosition[]) => JSX.Element;
  groupKey: string;
  fxRates: FxRates;
}

interface State {
  positions: Map<string, FxConvertedPosition>;
}

class StreamingOpenPositionsCmp extends React.Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = {
      positions: new Map()
    };
  }

  // clear the existing state when the subscription key changes
  public componentDidUpdate(prevProps: Props) {
    if (prevProps.groupKey !== this.props.groupKey) {
      const state: State = {
        positions: new Map()
      };

      this.setState(state);
    }
  }

  public render() {
    const papiSubscribe = (client: SwanPublicClient, groupKey: string) => {
      return client.streamPositions(
        groupKey,
        this.consumeUpdate,
        undefined
      );
    };

    const positionsArray = Array
      .from(this.state.positions.values())
      .map(this.applyFxRate);

    return (
      <SubscriptionLifecycle
        papiSubscribe={papiSubscribe}
        groupKey={this.props.groupKey}
      >
        {() => {
          return this.props.children(positionsArray);
        }}
      </SubscriptionLifecycle>
    );
  }

  private consumeUpdate = (newData: Array<StreamUpdate<Position>>) => {
    const positions = this.state.positions;

    newData.forEach(update => {
      const key = update.update.PositionKey.Key;
      switch (update.type) {
        case StreamUpdateType.Delete:
          positions.delete(key);
          break;
        default:
          positions.set(key, {
            ...update.update,
            BaseCurrencyOpenPnL: undefined,
            BaseCurrencyMarginRequirement: undefined
          });
          break;
      }
    });

    this.setState({ positions });
  }

  private applyFxRate = (
    position: Position | FxConvertedPosition
  ): FxConvertedPosition => {
    const fxRate = this.props.fxRates.getAndOrSubscribeFxRate(
      position.CurrencyId.toString(),
      this.props.baseCurrency.Key
    );
    const baseCurrencyOpenPnl = fxRate !== undefined
      ? position.OpenPnL * fxRate
      : undefined;
    const baseCurrencyMarginRequirement = fxRate !== undefined
      ? position.MarginRequirement * fxRate
      : undefined;

    return {
      ...position,
      BaseCurrencyOpenPnL: baseCurrencyOpenPnl,
      BaseCurrencyMarginRequirement: baseCurrencyMarginRequirement
    };
  }
}

const mapStateToProps = (state: AppState): PropsFromAppState => {
  return {
    baseCurrency: state.clientAccount.account!.Currency
  };
};

export const StreamingOpenPositions = connect(
  mapStateToProps
)(StreamingOpenPositionsCmp);
